/**
 * /accounting/export — Accountant Export Pack (Phase 11).
 *
 * Date-ranged export of the books to QuickBooks Desktop (.IIF) or
 * a generic CSV pack (Chart of Accounts, JEs, Trial Balance, Customers,
 * Vendors, Bills, Payments). Pure client — no backend required.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAP } from "@/data/accounting/ap/APContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import {
  buildIifExport, buildCsvPack, summarizeExport,
} from "@/data/accounting/export/exportFormats";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const today = () => new Date().toISOString().slice(0, 10);

function downloadBlob(filename: string, mime: string, contents: string) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AccountantExportPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries } = useGeneralLedger();
  const { bills, payments } = useAP();
  const { vendors } = useVendorPO();

  // Default range = current year-to-date
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [fromDate, setFromDate] = useState<string>(yearStart);
  const [toDate, setToDate] = useState<string>(today());
  const [includeVoided, setIncludeVoided] = useState(false);

  const summary = useMemo(
    () => summarizeExport({
      journalEntries, bills, payments, vendors,
      options: { fromDate, toDate, includeVoided },
    }),
    [journalEntries, bills, payments, vendors, fromDate, toDate, includeVoided],
  );

  const tag = `${fromDate}_to_${toDate}`;

  const downloadIif = () => {
    const contents = buildIifExport({
      accounts, journalEntries, vendors,
      options: { fromDate, toDate, includeVoided },
    });
    downloadBlob(`koredor_books_${tag}.iif`, "application/octet-stream", contents);
  };

  const downloadCsvPack = () => {
    const files = buildCsvPack({
      accounts, journalEntries, vendors, bills, payments,
      options: { fromDate, toDate, includeVoided },
    });
    files.forEach((f, i) => {
      // Stagger downloads so browsers don't block consecutive triggers
      setTimeout(() => downloadBlob(f.filename, "text/csv;charset=utf-8", f.contents), i * 250);
    });
  };

  const setQuickRange = (months: number) => {
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - months);
    setFromDate(start.toISOString().slice(0, 10));
    setToDate(end.toISOString().slice(0, 10));
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to General Ledger
        </Link>
        <h1 data-tour-id="accountant-export-title" className="text-2xl font-semibold tracking-tight">Accountant Export Pack</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Hand off the books for parallel-run with QuickBooks. Pick a date range, pick a format.
        </p>
      </div>

      <CockpitSection>
        {/* Range picker */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-8" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-8" />
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setQuickRange(1)}>Last month</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickRange(3)}>Last 3mo</Button>
            <Button variant="outline" size="sm" onClick={() => { setFromDate(yearStart); setToDate(today()); }}>YTD</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickRange(12)}>Last 12mo</Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input
            id="incl-void"
            type="checkbox"
            checked={includeVoided}
            onChange={e => setIncludeVoided(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="incl-void" className="text-xs">
            Include voided entries ({summary.voidedCount} in range)
          </label>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi label="Journal entries" value={String(summary.journalEntryCount)} sub={`${summary.journalLineCount} lines`} />
          <Kpi label="Total debits" value={fmt(summary.totalDebits)} />
          <Kpi label="Total credits" value={fmt(summary.totalCredits)} />
          <Kpi
            label="Balance check"
            value={summary.balanced ? "Balanced" : "OUT OF BALANCE"}
            tone={summary.balanced ? "text-emerald-600 dark:text-emerald-500" : "text-destructive"}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Kpi label="Bills" value={String(summary.billCount)} />
          <Kpi label="Bill payments" value={String(summary.paymentCount)} />
          <Kpi label="Vendors (active)" value={String(summary.vendorCount)} />
          <Kpi label="Customers w/ activity" value={String(summary.customerCount)} />
        </div>

        {!summary.balanced && summary.journalEntryCount > 0 && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
            <div>
              <div className="font-medium text-destructive">Filtered range is out of balance</div>
              <div className="text-xs text-muted-foreground">
                Debits and credits don't match. This usually means a date crosses a JE
                — widen the range or include voided entries to balance.
              </div>
            </div>
          </div>
        )}

        {/* Format cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormatCard
            icon={<FileText className="h-5 w-5" />}
            title="QuickBooks Desktop · IIF"
            description="One .iif file with Chart of Accounts, Customers, Vendors, and every JE as a General Journal transaction. Import via QB Desktop → File → Utilities → Import → IIF Files."
            bullets={[
              "Account types mapped (BANK / AR / AP / INC / COGS / etc.)",
              "Debits positive, credits negative per IIF spec",
              "Trade tagged as QB CLASS",
            ]}
            disabled={summary.journalEntryCount === 0}
            onDownload={downloadIif}
            downloadLabel="Download .iif"
          />
          <FormatCard
            icon={<Download className="h-5 w-5" />}
            title="Generic CSV Pack"
            description="7 CSV files for QB Online, Xero, Sage, or any accountant who lives in Excel. Includes trial balance snapshot for the range."
            bullets={[
              "01 Chart of Accounts · 02 Journal Entries · 03 Trial Balance",
              "04 Customers · 05 Vendors (with W-9/1099)",
              "06 Bills · 07 Bill Payments",
            ]}
            disabled={summary.journalEntryCount === 0}
            onDownload={downloadCsvPack}
            downloadLabel="Download CSV pack (7 files)"
          />
        </div>

        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          <strong>Parallel-run guidance:</strong> export each month-end, hand to your accountant
          alongside the QB export, and reconcile the trial balance to the penny. After 3 clean months,
          you can retire the QB side. Draft JEs and voided entries are excluded by default — toggle
          above to include voids when reconciling reversal activity.
        </p>
      </CockpitSection>
    </CockpitPage>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tone ?? ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function FormatCard({
  icon, title, description, bullets, disabled, onDownload, downloadLabel,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets: string[];
  disabled: boolean;
  onDownload: () => void;
  downloadLabel: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-2 text-foreground">
        {icon}
        <div className="font-medium">{title}</div>
      </div>
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{description}</p>
      <ul className="text-[11px] text-muted-foreground mb-4 space-y-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onDownload} disabled={disabled} className="w-full" size="sm">
        <Download className="h-3.5 w-3.5 mr-1.5" />
        {downloadLabel}
      </Button>
    </div>
  );
}
